import { MaskBase } from "luxedo-canvas/asset"
import { MaskRepository } from "../../modules/MaskRepository"
import { ImageEditor, Resolution, ShapeOption } from "../../modules/ImageEditor"
import { SidebarController } from "./sidebar/SidebarController.svelte"

export type ImageSaveCallback = (imgBlob: Blob) => void
type ViewType = "simple" | "complex"
export class ImageModifierController {
	imagePath: string
	imageResolution: Resolution
	maskRepository: MaskRepository
	imageEditor: ImageEditor

	triggerSave: ImageSaveCallback
	onCancel: () => void

	selectedMasks: MaskBase[] = $state([])
	availableMasks: MaskBase[] = $state([])
	view: ViewType = $state("simple")

	isLoadingMasks: boolean = $state(false)

	// base 64 image
	modifiedImage: string = $state()

	isRemoved = $state(false)

	constructor(
		imagePath: string,
		imageResolution: Resolution,
		imageEditor: ImageEditor,
		maskRepository: MaskRepository,
		triggerSave: ImageSaveCallback,
		onCancel: () => void
	) {
		this.imagePath = imagePath
		this.imageResolution = imageResolution
		this.maskRepository = maskRepository
		this.imageEditor = imageEditor
		this.triggerSave = triggerSave
		this.onCancel = onCancel

		this.refreshMasks()
	}

	maskLoadingProimse: Promise<void>

	setImageEditor(editor: ImageEditor) {
		this.imageEditor = editor
		this.selectedMasks = []
	}

	refreshMasks = async () => {
		this.maskLoadingProimse = new Promise(async (res) => {
			this.isLoadingMasks = true
			await this.maskRepository.refresh()
			this.availableMasks = await this.maskRepository.listAll()
			this.isLoadingMasks = false
			return res()
		})
	}

	/**
	 * Prompt the user to create a new mask.
	 * Save the mask and select it.
	 */
	createMask = async (shape: ShapeOption, section: string, onLoading?: () => void) => {
		const mask = await this.imageEditor.createMask(shape, section)
		if (onLoading) onLoading()
		const refreshedMask = await this.addMask(mask)
		this.selectMask(refreshedMask)
		await this.refreshMasks()
		return refreshedMask
	}

	/**
	 * Save the mask to the mask repository and select it.
	 */
	addMask = async (mask: MaskBase) => {
		return await this.saveMask(mask)
	}

	/**
	 * Prompt the user to edit the selected mask.
	 */
	editMask = async (mask: MaskBase) => {
		const updatedMask = await this.imageEditor.editMask(mask)

		await this.saveMask(updatedMask)
		await this.refreshMasks()

		const refreshedMask = this.availableMasks.find((m) => m.id === mask.id)

		this.deselectMask(mask)
		this.selectMask(refreshedMask)
	}

	/**
	 * Select the specified mask and render it on the canvas.
	 */
	selectMask = (mask: MaskBase) => {
		this.selectedMasks.push(mask)
		this.imageEditor.showMask(mask)
	}

	/**
	 * Deselect the specified mask and remove it from the canvas.
	 */
	deselectMask = (mask: MaskBase) => {
		const index = this.selectedMasks.findIndex((base) => base.id === mask.id)

		if (index > -1) {
			this.selectedMasks.splice(index, 1)
			this.imageEditor.hideMask(mask)
		}
	}

	/**
	 * Save the specified mask to the mask repository
	 */
	saveMask = async (mask: MaskBase) => {
		return await this.maskRepository.save(mask)
	}

	/**
	 * Save an array of masks
	 */
	saveMasks = async (masks: MaskBase[]) => {
		const promises: Promise<MaskBase>[] = []
		for (const mask of masks) {
			promises.push(this.saveMask(mask))
		}

		return await Promise.all(promises)
	}

	// denyRemoval = async () => {
	// 	this.imageEditor.reset()
	// }

	/**
	 * Save the modified image
	 */
	approveRemoval = async () => {
		if (!this.modifiedImage) throw new Error("No image has been selected to approve.")

		const blob = base64ToBlob(this.modifiedImage)
		await this.triggerSave(blob)
	}

	denyRemoval = async (retrigger = true) => {
		this.modifiedImage = undefined
		if (retrigger) this.triggerRemoval()
	}

	/**
	 * Triggers the background removal mode, and sets the resulting image as this.modified image
	 */
	triggerRemoval = async (): Promise<string> => {
		this.isRemoved = false
		SidebarController.setView("remove")

		try {
			const img = await this.imageEditor.removeBackground(this.selectedMasks)
			this.modifiedImage = img
			this.isRemoved = true
			return img
		} catch (e) {
			console.warn("User cancelled background removal")
		}
	}
}

function base64ToBlob(base64: string, mimeType: string = "image/jpeg"): Blob {
	const base64Data = base64.includes(",") ? base64.split(",")[1] : base64
	const byteCharacters = atob(base64Data)
	const byteArray = Uint8Array.from(byteCharacters, (char) => char.charCodeAt(0))
	return new Blob([byteArray], { type: mimeType })
}
